import React, { useCallback, useEffect, useState } from "react";
import { Plus, RefreshCw } from "lucide-react";
import { apiService } from "../../services/api";
import type { User } from "../../types/auth";
import { LoadingSpinner } from "../common/LoadingSpinner";
import { UserFilters } from "./UserFilters";
import { UserTable } from "./UserTable";
import { UserModal } from "./UserModal";
import { TemporaryPasswordModal } from "./TemporaryPasswordModal";

export const UsersList: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [fleetFilter, setFleetFilter] = useState("");
  const [activeOnly, setActiveOnly] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"create" | "edit">("create");
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [error, setError] = useState("");
  const [actionLoadingId, setActionLoadingId] = useState<string | null>(null);
  const [tempPassword, setTempPassword] = useState<string | null>(null);

  const limit = 20;

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, roleFilter, fleetFilter, activeOnly]);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const result = await apiService.getUsers({
        page,
        limit,
        email: debouncedSearch.trim() || undefined,
        role: roleFilter || undefined,
        fleet_id: fleetFilter || undefined,
        is_active: activeOnly ? true : undefined,
      });
      setUsers(result.users || []);
      setTotalPages(result.pagination?.totalPages ?? 1);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load users");
    } finally {
      setLoading(false);
    }
  }, [page, debouncedSearch, roleFilter, fleetFilter, activeOnly]);

  const runUserAction = useCallback(
    async (user: User, action: () => Promise<void>) => {
      setActionLoadingId(user.id);
      setError("");
      try {
        await action();
        await loadUsers();
      } catch (err) {
        setError(err instanceof Error ? err.message : "Action failed");
      } finally {
        setActionLoadingId(null);
      }
    },
    [loadUsers],
  );

  useEffect(() => {
    loadUsers().catch(console.error);
  }, [loadUsers]);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Users</h1>
          <p className="text-gray-600 mt-2">
            Manage user accounts, roles, and fleet access
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => loadUsers()}
            className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </button>
          <button
            type="button"
            onClick={() => {
              setModalMode("create");
              setSelectedUser(null);
              setModalOpen(true);
            }}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add User
          </button>
        </div>
      </div>

      {error && (
        <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
          {error}
        </p>
      )}

      <UserFilters
        search={search}
        onSearchChange={setSearch}
        roleFilter={roleFilter}
        onRoleFilterChange={setRoleFilter}
        fleetFilter={fleetFilter}
        onFleetFilterChange={setFleetFilter}
        activeOnly={activeOnly}
        onActiveOnlyChange={setActiveOnly}
      />

      {loading ? (
        <LoadingSpinner />
      ) : (
        <UserTable
          users={users}
          actionLoadingId={actionLoadingId}
          onEdit={(user) => {
            setModalMode("edit");
            setSelectedUser(user);
            setModalOpen(true);
          }}
          onActivate={(user) =>
            runUserAction(user, async () => {
              await apiService.updateUser(user.id, { is_active: true });
            })
          }
          onDeactivate={(user) => {
            if (!confirm(`Deactivate user ${user.email}?`)) return;
            runUserAction(user, () => apiService.deactivateUser(user.id));
          }}
          onResetPassword={(user) => {
            if (
              !confirm(
                `Reset password for ${user.email}? A new temporary password will be generated.`,
              )
            ) {
              return;
            }
            runUserAction(user, async () => {
              const result = await apiService.resetUserPassword(user.id);
              if (result.temporary_password) {
                setTempPassword(result.temporary_password);
              }
            });
          }}
        />
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="px-3 py-1 border rounded disabled:opacity-50"
          >
            Previous
          </button>
          <span className="text-sm text-gray-600">
            Page {page} of {totalPages}
          </span>
          <button
            type="button"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
            className="px-3 py-1 border rounded disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}

      <UserModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        mode={modalMode}
        user={selectedUser}
        onSaved={() => loadUsers()}
      />

      {tempPassword && (
        <TemporaryPasswordModal
          password={tempPassword}
          onClose={() => setTempPassword(null)}
        />
      )}
    </div>
  );
};
